// src/pages/Hunt.jsx
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Map2 from '../components/Map2'
import { fetchHunt, markTreasureFound } from '../utils/kv'
import { getProgress, saveProgress, getHuntCache, initOfflineDB } from '../utils/offline'
import {
  startProximityWatch,
  stopProximityWatch,
  setProximityTarget,
  clearProximityTarget
} from '../utils/proximity'

export default function Hunt() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [hunt, setHunt] = useState(null)
  const [progress, setProgress] = useState(null)
  const [currentTreasure, setCurrentTreasure] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tapCount, setTapCount] = useState(0)

  useEffect(() => {
    async function initialize() {
      try {
        await initOfflineDB()

        // Check if hunt has been updated since last load
        const lastHuntHash = localStorage.getItem('lastHuntHash')
        let huntData = await fetchHunt()

        if (huntData) {
          // Create a hash of the hunt data to detect changes
          const currentHash = JSON.stringify(huntData)

          // If hunt has changed, use the new version
          if (lastHuntHash !== currentHash) {
            localStorage.setItem('lastHuntHash', currentHash)
          }
        } else {
          // Fall back to cached version if fetch fails
          huntData = await getHuntCache()
        }

        if (!huntData) {
          setError(t('hunt.notFound'))
          setLoading(false)
          return
        }

        const prog = await getProgress()
        setHunt(huntData)
        setProgress(prog)

        // Update hunt treasures with found status from progress
        if (prog && prog.foundTreasures) {
          const foundIds = new Set(prog.foundTreasures.map(t => t.id))
          const updatedHunt = {
            ...huntData,
            treasures: huntData.treasures.map(t => ({
              ...t,
              found: foundIds.has(t.id)
            }))
          }
          setHunt(updatedHunt)
          huntData = updatedHunt
        }

        // Check if all treasures have been found based on progress
        const allFound = prog && prog.foundTreasures && prog.foundTreasures.length === huntData.treasures.length

        if (allFound) {
          navigate('/puzzle')
        } else {
          const current = huntData.treasures.find(t => !t.found)
          if (current) {
            setCurrentTreasure(current)
          }
        }

        setLoading(false)
      } catch (err) {
        console.error('Initialization error:', err)
        setError('Failed to load hunt data')
        setLoading(false)
      }
    }

    initialize()
  }, [navigate])

  async function handleTreasureSubmit(treasureId, keyword) {
    const treasure = hunt.treasures.find(t => t.id === treasureId)
    if (!treasure || keyword.toLowerCase() !== treasure.keyword.toLowerCase()) {
      return
    }

    await completeTreasure(treasureId, keyword, 'keyword')
  }

  async function completeTreasure(treasureId, keyword, method) {
    if (!hunt || !progress) return

    // Guard against a duplicate arrival for a treasure already recorded.
    if (progress.foundTreasures.some(t => t.id === treasureId)) {
      return
    }

    const newProgress = {
      ...progress,
      foundTreasures: [
        ...progress.foundTreasures,
        { id: treasureId, keyword, submittedAt: Date.now(), method }
      ]
    }
    await saveProgress(newProgress)
    setProgress(newProgress)

    const newHunt = {
      ...hunt,
      treasures: hunt.treasures.map(t =>
        t.id === treasureId ? { ...t, found: true } : t
      )
    }
    setHunt(newHunt)

    // Sync to KV but don't block on it - progress is saved locally
    markTreasureFound(treasureId).catch(err => console.warn('KV sync failed (non-critical):', err))

    const nextTreasure = newHunt.treasures.find(t => !t.found)
    if (nextTreasure) {
      setCurrentTreasure(nextTreasure)
    } else {
      setTimeout(() => {
        navigate('/puzzle')
      }, 500)
    }
  }

  const isProximityHunt = hunt?.unlockMode === 'proximity'

  // Keep a ref to the latest completeTreasure so the window listener below can
  // be registered once without capturing stale hunt/progress state.
  const completeTreasureRef = useRef(null)
  completeTreasureRef.current = completeTreasure

  // Subscribe to arrivals once, for the lifetime of the page.
  useEffect(() => {
    const handleArrived = (event) => {
      const treasureId = event.detail?.treasureId
      if (treasureId) {
        completeTreasureRef.current(treasureId, null, 'proximity')
      }
    }

    window.addEventListener('treasurearrived', handleArrived)
    return () => window.removeEventListener('treasurearrived', handleArrived)
  }, [])

  // Run the proximity engine only for proximity hunts.
  useEffect(() => {
    if (!isProximityHunt) return

    startProximityWatch()
    return () => stopProximityWatch()
  }, [isProximityHunt])

  // Aim the engine at whichever treasure the player is walking towards.
  useEffect(() => {
    if (!isProximityHunt) return

    if (currentTreasure) {
      setProximityTarget(currentTreasure)
    } else {
      clearProximityTarget()
    }
  }, [isProximityHunt, currentTreasure?.id])

  async function handleResetHunt() {
    if (confirm('Are you sure you want to restart the hunt? All progress will be lost.')) {
      const emptyProgress = { foundTreasures: [] }
      await saveProgress(emptyProgress)
      setProgress(emptyProgress)

      const resetHunt = {
        ...hunt,
        treasures: hunt.treasures.map(t => ({ ...t, found: false }))
      }
      setHunt(resetHunt)
      setCurrentTreasure(resetHunt.treasures[0])
      setTapCount(0)
    }
  }

  function handleProgressTextTap() {
    setTapCount(prev => {
      const newCount = prev + 1
      if (newCount === 3) {
        handleResetHunt()
        return 0
      }
      return newCount
    })
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <p>{t('hunt.loading')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.container}>
        <p style={{ color: 'red' }}>{error}</p>
        <a href="/admin" style={{ color: '#007bff' }}>{t('hunt.goToAdmin')}</a>
      </div>
    )
  }

  const foundCount = progress?.foundTreasures.length || 0

  return (
    <div style={styles.hunt}>
      <Map2
        treasure={currentTreasure}
        foundTreasures={hunt?.treasures.filter(t => t.found) || []}
        onTreasureTap={handleTreasureSubmit}
        unlockMode={hunt?.unlockMode || 'keyword'}
      />

      {/* Store hunt data on window for Map2 to access */}
      {hunt && (() => { window.__hunt = hunt; return null; })()}

      <div style={styles.progressBar}>
        <div style={{
          ...styles.progressFill,
          width: `${(foundCount / hunt.treasures.length) * 100}%`
        }} />
      </div>

      <div style={styles.progressText} onClick={handleProgressTextTap}>
        {t('hunt.foundProgress', { count: foundCount, total: hunt.treasures.length })}
      </div>
    </div>
  )
}

const styles = {
  container: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  hunt: {
    width: '100%',
    height: '100vh',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column'
  },
  progressBar: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '8px',
    background: '#e9ecef',
    zIndex: 100
  },
  progressFill: {
    height: '100%',
    background: '#28a745',
    transition: 'width 0.3s'
  },
  progressText: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'rgba(0,0,0,0.7)',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    zIndex: 100,
    cursor: 'pointer',
    userSelect: 'none'
  }
}
